import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type PropsWithChildren } from "react";
import { api, type Schema } from "../../api/client";
import { ApiProblem } from "../../api/problemDetails";

export type Capability = "content.edit" | "workflow.operate" | "workspace.manage" | "publishing.manage" | "providers.manage" | "platform.providers.manage" | "platform.render-templates.manage" | "platform.thumbnail-templates.manage" | "platform.caption-styles.manage" | "platform.security.read" | "operations.diagnostics" | "operations.audit" | "costs.read" | "budgets.manage";
export type AuthState = "loading" | "anonymous" | "authenticated";

interface AuthContextValue {
  state: AuthState;
  session: Schema["SessionResponse"] | null;
  login(email: string, password: string): Promise<void>;
  logout(): Promise<void>;
  refresh(): Promise<void>;
  can(capability: Capability): boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function allows(roles: string[] | undefined, capability: Capability): boolean {
  const values = new Set(roles ?? []);
  if (capability === "content.edit") return values.has("Owner") || values.has("Editor");
  if (capability === "workflow.operate") return values.has("Owner") || values.has("Operator");
  if (capability === "workspace.manage") return values.has("Owner");
  if (capability === "publishing.manage") return values.has("Owner");
  if (capability === "providers.manage") return values.has("Owner");
  if (capability === "costs.read") return values.has("Owner") || values.has("Operator") || values.has("Analyst") || values.has("Auditor");
  if (capability === "budgets.manage") return values.has("Owner");
  if (capability === "platform.providers.manage" || capability === "platform.render-templates.manage" || capability === "platform.thumbnail-templates.manage" || capability === "platform.caption-styles.manage" || capability === "platform.security.read") return false;
  if (capability === "operations.diagnostics") return values.has("Owner") || values.has("Operator");
  return values.has("Owner") || values.has("Auditor");
}

export function AuthProvider({ children }: PropsWithChildren): React.JSX.Element {
  const [state, setState] = useState<AuthState>("loading");
  const [session, setSession] = useState<Schema["SessionResponse"] | null>(null);
  const sessionRevision = useRef(0);

  const clearSession = useCallback(() => {
    sessionRevision.current += 1;
    api.clearSessionState();
    setSession(null);
    setState("anonymous");
  }, []);

  const refresh = useCallback(async () => {
    const revision = ++sessionRevision.current;
    try {
      const current = await api.session();
      await api.refreshCsrf();
      if (revision !== sessionRevision.current) return;
      setSession(current.data);
      setState("authenticated");
    } catch (error) {
      if (revision !== sessionRevision.current) return;
      clearSession();
      if (!(error instanceof ApiProblem) || !error.isSessionExpired) throw error;
    }
  }, [clearSession]);

  useEffect(() => { void refresh().catch(() => undefined); }, [refresh]);
  useEffect(() => {
    window.addEventListener("contentforge:session-expired", clearSession);
    return () => window.removeEventListener("contentforge:session-expired", clearSession);
  }, [clearSession]);

  const login = useCallback(async (email: string, password: string) => {
    sessionRevision.current += 1;
    await api.login({ email, password });
    await refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try { await api.logout(); }
    finally { clearSession(); }
  }, [clearSession]);

  const value = useMemo<AuthContextValue>(() => ({
    state,
    session,
    login,
    logout,
    refresh,
    can: capability => capability === "platform.providers.manage" || capability === "platform.render-templates.manage" || capability === "platform.thumbnail-templates.manage" || capability === "platform.caption-styles.manage" || capability === "platform.security.read"
      ? session?.isPlatformAdmin === true
      : allows(session?.roles, capability)
  }), [login, logout, refresh, session, state]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider.");
  return context;
}
